/* ============================================================
   OnboardingNudge — TASK-144, issue #237 (owner design 2026-08-31)
   "Recommend, don't force." The preview paywall (TASK-143) already lets a
   new user skip onboarding straight into the live product; this is the
   frequent-but-not-annoying reminder that nudges them back to finish it —
   in-app, or in their native channel (ChatGPT/MCP), instead of a hard gate.

   Gated end-to-end behind nudgesEnabled() ('dathent.nudges.enabled', DARK
   default OFF, see lib/flags.js). While off this renders nothing and does
   no extra network calls beyond what RequireOnboarding (App.jsx) already
   makes on route entry.

   Incomplete-onboarding detection mirrors RequireOnboarding exactly:
   api.profile.getBrandProfile() 404 = onboarding genuinely unfinished
   (the brand profile row is created at onboarding completion); any OTHER
   error (network blip, 5xx, auth hiccup) fails quiet — never show on an
   error we can't interpret. The client already caches that GET for 5 min
   (services/api/client.js), so re-checking here is cheap.

   Cadence: "frequent, not annoying" — check on mount (every /app visit)
   and on window focus, NEVER a setInterval poll. After the user dismisses
   (the × ), the banner stays hidden for `dathent.nudge.hours` hours
   (default 24) via a per-user localStorage timestamp — clicking one of the
   external actions (ChatGPT / docs) does NOT count as a dismiss, so the
   banner is still there if they come back without finishing.

   Mounted once inside PreviewProvider (App.jsx, TASK-143 slot) as a sibling
   of its children — never inside the /onboarding route tree, so it can't
   double up with the screen the user is already on.
   ============================================================ */
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { Icon } from './Icons.jsx';
import { api } from '../services/api/client.js';
import { useAuth } from '../contexts/AuthContext';
import { nudgesEnabled } from '../lib/flags.js';
import { track } from '../lib/funnel.js';
import { usePendingOAuth } from '../contexts/PendingOAuthContext.jsx';
import { isChatGptOrigin } from '../lib/signupSource.js';
import { onboardingNudgeCopy, onboardingNudgePresentation } from '../lib/onboardingNudgeState.js';
import QuickBrandSheet from './QuickBrandSheet.jsx';
import { useDashboardData } from '../state/DashboardDataContext.jsx';
import { isBareWorkspace } from '../lib/brandWorkspace.js';
import { freePublishingState } from '../lib/freePublishing.js';
import { usePersonalPublishingStart } from '../hooks/usePersonalPublishingStart.js';
import { useFreeOnboardingStart } from '../hooks/useFreeOnboardingStart.js';
import { isFirstPostOnboarding } from '../lib/freeOnboarding.js';
import { useFreePreview } from '../contexts/FreePreviewContext.jsx';

// Owner-approved links (2026-08-31, issue #237 comments): the official app
// in ChatGPT's catalog (approved 2026-08-19) and the /connect instructions
// page, which already links the same ChatGPT app — this is the channel with
// the 100% signup->activation rate (#200), so nudges point users there.
const CHATGPT_URL = 'https://chatgpt.com/plugins/plugin_asdk_app_6a6172c418e481918bf3817aa288e3f0';
const CONNECT_DOCS_URL = 'https://dathent.ai/connect';

const DISMISS_KEY_PREFIX = 'dathent.nudge.onboarding.lastDismiss';
const HOURS_KEY = 'dathent.nudge.hours';
const DEFAULT_HOURS = 24;

function dismissKey(userId) {
  return `${DISMISS_KEY_PREFIX}.${userId || 'anon'}`;
}

function readLastDismiss(userId) {
  try {
    const n = parseInt(window.localStorage.getItem(dismissKey(userId)) ?? '', 10);
    return Number.isFinite(n) && n > 0 ? n : 0;
  } catch (_) {
    return 0;
  }
}

function writeLastDismiss(userId) {
  try {
    window.localStorage.setItem(dismissKey(userId), String(Date.now()));
  } catch (_) { /* storage unavailable — worst case the nudge reappears sooner */ }
}

function readIntervalHours() {
  try {
    const raw = window.localStorage.getItem(HOURS_KEY);
    const n = parseFloat(raw ?? '');
    return Number.isFinite(n) && n > 0 ? n : DEFAULT_HOURS;
  } catch (_) {
    return DEFAULT_HOURS;
  }
}

function cadenceElapsed(userId) {
  const last = readLastDismiss(userId);
  if (!last) return true;
  return Date.now() - last >= readIntervalHours() * 3600 * 1000;
}

export default function OnboardingNudge() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const preview = useFreePreview();
  const { user } = useAuth();
  const pendingOAuth = usePendingOAuth();
  const dashboard = useDashboardData();
  const freePublishing = Boolean(freePublishingState(dashboard.entitlementsError ? null : dashboard.entitlements));
  const personalStart = usePersonalPublishingStart(freePublishing && nudgesEnabled());
  const freeStart = useFreeOnboardingStart(freePublishing && nudgesEnabled());
  const postingPlan = dashboard.entitlements?.plan === 'posting';
  const [visible, setVisible] = useState(false);
  const [quickBrandOpen, setQuickBrandOpen] = useState(false);
  const inlineSetup = freePublishing && location.pathname === '/app' && preview.active && isFirstPostOnboarding(preview.entitlements);

  // Check on mount (every /app visit) and on window focus — deliberately NO
  // setInterval: the task calls for "frequent, not spammy," and re-checking
  // on every tab-focus already covers a user leaving the tab open for hours.
  useEffect(() => {
    if (!nudgesEnabled() || !user || inlineSetup) return;
    if (!dashboard.entitlements) {
      dashboard.invalidateResource?.('entitlements');
      return;
    }
    let cancelled = false;

    const check = async () => {
      if (!nudgesEnabled() || !cadenceElapsed(user.id)) return;
      try {
        const response = await api.profile.getBrandProfile();
        const brand = (response?.data ?? response)?.brandProfile || null;
        // TASK (2026-09-11, bare-brand-prompts #3): a present-but-bare brand
        // now gets the prepare-brand nudge on ANY plan, not only Posting —
        // owner finding 2026-09-10: a Scheduler account that upgrades to
        // Starter keeps its auto-created bare workspace and saw no reminder
        // to finish it. Was `postingPlan && isBareWorkspace(brand)`; the
        // postingPlan gate is gone, the copy below (title/CTA) still branches
        // on postingPlan so Posting's own wording is unchanged.
        if (isBareWorkspace(brand)) {
          if (!cancelled) {
            setVisible(true);
            // An impression is not a dismissal. In particular, a blocked
            // onboarding attempt must survive route changes/remounts.
            try { track('nudge_shown', { kind: 'prepare_brand' }); } catch (_) { /* fail-silent */ }
          }
        } else if (!cancelled) {
          // A non-bare profile means the brand is properly set up.
          setVisible(false);
        }
      } catch (err) {
        if (cancelled) return;
        if (err?.status === 404 && !postingPlan) {
          setVisible(true);
          try { track('nudge_shown', { kind: 'onboarding' }); } catch (_) { /* fail-silent */ }
        }
        // Any other error: fail-quiet, stay hidden.
      }
    };

    check();
    window.addEventListener('focus', check);
    return () => {
      cancelled = true;
      window.removeEventListener('focus', check);
    };
  }, [user, dashboard.entitlements, dashboard.invalidateResource, postingPlan, inlineSetup]);

  const presentation = onboardingNudgePresentation({
    visible,
    pluginOrigin: isChatGptOrigin(pendingOAuth.pending, user),
    connectionTransitionActive: pendingOAuth.connectionTransitionActive,
  });
  if (postingPlan) presentation.showPluginActions = false;
  if (!presentation.show || inlineSetup) return null;
  const copy = onboardingNudgeCopy(postingPlan);

  const dismiss = () => {
    setVisible(false);
    writeLastDismiss(user?.id);
    try { track('nudge_dismissed', { kind: 'onboarding' }); } catch (_) { /* fail-silent */ }
  };

  const clickAction = (action) => {
    try { track('nudge_clicked', { kind: 'onboarding', action }); } catch (_) { /* fail-silent */ }
  };

  return (
    <>
    <div className="onboarding-nudge" role="status" aria-live="polite">
      <button
        type="button"
        className="onboarding-nudge__close"
        aria-label={t('onboardingNudge.close')}
        onClick={dismiss}
      >
        <Icon name="x" size={13} />
      </button>
      <div className="onboarding-nudge__header">
        <span className="onboarding-nudge__icon"><Icon name="bell" size={16} /></span>
        <p className="onboarding-nudge__title">{t(freePublishing ? (freeStart.available ? (isFirstPostOnboarding(dashboard.entitlements) || dashboard.entitlements?.freeOnboarding?.draftsLimit === 1 ? 'firstPostOnboarding.included' : 'onboardingNudge.freeOnce') : 'onboardingNudge.freeUnavailable') : copy.titleKey)}</p>
      </div>
      <div className="onboarding-nudge__actions">
        <button
          type="button"
          className="onboarding-nudge__cta"
          disabled={freePublishing && freeStart.busy}
          onClick={() => {
            clickAction('onboarding');
            if (freePublishing && freeStart.available) freeStart.start();
            else navigate(freePublishing ? '/app/plans' : '/onboarding');
          }}
        >
          {t(freePublishing && !freeStart.available ? 'freePublishing.viewPlans' : copy.ctaKey)}
        </button>
        {copy.showQuickBrandCta && <button
          type="button"
          className="onboarding-nudge__cta onboarding-nudge__cta--secondary"
          disabled={freePublishing ? personalStart.disabled : undefined}
          onClick={() => {
            clickAction('quick_brand');
            if (freePublishing) personalStart.start('create');
            else setQuickBrandOpen(true);
          }}
        >
          {t(freePublishing && personalStart.busy ? 'freePublishing.preparing' : 'onboardingNudge.withoutBrandCta')}
        </button>}
      </div>
      {freePublishing && personalStart.error && <p role="alert">{t('freePublishing.startError')}</p>}
      {freePublishing && freeStart.error && <p role="alert">{t('onboardingNudge.freeStartError')}</p>}
      {presentation.showPluginActions && (
        <div className="onboarding-nudge__plugin-actions">
          <a href={CHATGPT_URL} target="_blank" rel="noopener noreferrer" className="onboarding-nudge__docs" onClick={() => clickAction('chatgpt')}>
            {t('onboardingNudge.chatgptCta')}
          </a>
          <a href={CONNECT_DOCS_URL} target="_blank" rel="noopener noreferrer" className="onboarding-nudge__docs" onClick={() => clickAction('docs')}>
            {t('onboardingNudge.docsLink')}
          </a>
        </div>
      )}
    </div>
    {copy.showQuickBrandCta && !freePublishing && <QuickBrandSheet
      open={quickBrandOpen}
      source="nudge"
      onClose={() => setQuickBrandOpen(false)}
      onCreated={() => { setQuickBrandOpen(false); setVisible(false); }}
    />}
    </>
  );
}
